import React, { useRef, useState, useEffect } from "react";
import Modal from "./Modal";
import MinimalAudioPlayer from "./MinimalAudioPlayer";
import { FaMicrophone } from "react-icons/fa";
import { RiSendPlaneFill } from "react-icons/ri";
import { useTheme } from "hooks/useTheme";
import { isColorDark } from "utils/utilityFunctions";
import { useSendMessage } from "hooks/useSendMessage";
type Props = {
  isOpen: boolean;
  close: () => void;
};

const playBtnStyle = {
  padding: "10px 20px",
  borderRadius: "9999px", // pill shape
  // red for recording
  color: "white",
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
};

const AudioRecorder: React.FC<Props> = ({ isOpen, close }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [send, loadingSend] = useSendMessage(close);

  const formatDuration = (seconds: number) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };
  const theme = useTheme();
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setAudioUrl(null);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone", err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    if (!isOpen && isRecording) {
      stopRecording();
    }
  }, [isOpen]);
  const handleSend = async () => {
    const response = await fetch(audioUrl);
    const blob = await response.blob();
    send({
      message: "voice message",
      audio: blob,
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={() => close()}>
      <div
        style={{ backgroundColor: theme.main, borderRadius: 8, padding: 20 }}
      >
        <style>
          {`
            
            .recording-indicator {
  margin: 20px;
  height: 130px;
  width: 130px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 70px;
  box-shadow: ${theme.primary};
}

.recording-pulse {
  animation: pulse 1.2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: ${theme.primary};
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
}

            `}
        </style>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            className={`${
              isRecording && "recording-pulse "
            } recording-indicator`}
            style={{ backgroundColor: theme.primary }}
          >
            <FaMicrophone style={{ color: "white" }} />
          </div>
        </div>

        {isRecording && (
          <>
            <div
              className="duration"
              style={{ color: isColorDark(theme.main) ? "white" : "black" }}
            >
              {formatDuration(duration)}
            </div>
          </>
        )}

        {audioUrl && <MinimalAudioPlayer src={audioUrl} />}
        <br />
        {!isRecording && (
          <button
            style={{ ...playBtnStyle, backgroundColor: theme.primary }}
            onClick={startRecording}
          >
            Start Recording
          </button>
        )}

        {isRecording && (
          <button
            style={{
              ...playBtnStyle,
              backgroundColor: theme.primary,
              fontSize: 25,
              marginTop: 20,
            }}
            disabled={!audioUrl || loadingSend}
            onClick={stopRecording}
          >
            ⏹
          </button>
        )}
        <br />
        <br />
        {!isRecording && (
          <button
            style={{
              ...playBtnStyle,
              backgroundColor: audioUrl ? "#0f1411" : "#333634",
              color: audioUrl ? "white" : "gray",
            }}
            disabled={!audioUrl || loadingSend}
            onClick={handleSend}
          >
            Send Audio{" "}
            <RiSendPlaneFill style={{ transform: "translateY(2px)" }} />
          </button>
        )}
        <br />
        <br />
      </div>
    </Modal>
  );
};

export default AudioRecorder;
